import React, { useState, useEffect } from 'react';
import {
	ChevronLeft,
	ChevronRight,
	Search,
	UserCheck,
	UserX,
	Users,
	UserPlus,
	AlertTriangle,
	Trash2,
	Edit2,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { adminAPI, usersAPI } from '../../services/api';
import { toast } from 'react-toastify';

const getDisplayName = (s) => {
	if (!s) return '—';
	const name = [s.firstName, s.lastName].filter(Boolean).join(' ').trim();
	return name || s.email || '—';
};

const SupervisorManagement = () => {
	const navigate = useNavigate();
	const [supervisors, setSupervisors] = useState([]);
	const [loading, setLoading] = useState(true);
	const [searchQuery, setSearchQuery] = useState('');
	const [selectedSupervisor, setSelectedSupervisor] = useState(null);
	const [assignedAgents, setAssignedAgents] = useState([]);
	const [agentsLoading, setAgentsLoading] = useState(false);
	const [actionLoading, setActionLoading] = useState(null);
	const [showDisableConfirm, setShowDisableConfirm] = useState(false);
	const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
	const [showEditModal, setShowEditModal] = useState(false);
	const [editForm, setEditForm] = useState({});
	const [editLoading, setEditLoading] = useState(false);

	useEffect(() => {
		fetchSupervisors();
	}, []);

	useEffect(() => {
		if (selectedSupervisor) {
			fetchAssignedAgents(selectedSupervisor.id);
		} else {
			setAssignedAgents([]);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [selectedSupervisor?.id]);

	const fetchSupervisors = async () => {
		try {
			setLoading(true);
			const data = await adminAPI.getSupervisors();
			setSupervisors(Array.isArray(data) ? data : []);
		} catch {
			toast.error('Failed to load supervisors');
			setSupervisors([]);
		} finally {
			setLoading(false);
		}
	};

	const fetchAssignedAgents = async (supervisorId) => {
		if (!supervisorId) return;
		try {
			setAgentsLoading(true);
			const data = await adminAPI.getSupervisorAgents(supervisorId);
			setAssignedAgents(Array.isArray(data) ? data : []);
		} catch {
			toast.error('Failed to load assigned agents');
			setAssignedAgents([]);
		} finally {
			setAgentsLoading(false);
		}
	};

	const filteredSupervisors = supervisors.filter(
		(s) =>
			getDisplayName(s).toLowerCase().includes(searchQuery.toLowerCase()) ||
			(s.email && s.email.toLowerCase().includes(searchQuery.toLowerCase())) ||
			(s.phone && s.phone.includes(searchQuery)),
	);

	const handleDisableSupervisor = async () => {
		if (!selectedSupervisor || actionLoading) return;
		setActionLoading('disable');
		try {
			await adminAPI.unassignAllAgentsFromSupervisor(selectedSupervisor.id);
			await usersAPI.update(selectedSupervisor.id, { isActive: false });
			toast.success('Supervisor disabled. All agents have been unassigned.');
			setShowDisableConfirm(false);
			setSelectedSupervisor((prev) => prev ? { ...prev, isActive: false } : null);
			fetchSupervisors();
			fetchAssignedAgents(selectedSupervisor.id);
		} catch (err) {
			toast.error(err.response?.data?.message || 'Failed to disable supervisor');
		} finally {
			setActionLoading(null);
		}
	};

	const handleEnableSupervisor = async () => {
		if (!selectedSupervisor || actionLoading) return;
		setActionLoading('enable');
		try {
			await usersAPI.update(selectedSupervisor.id, { isActive: true });
			toast.success('Supervisor enabled. They can log in again.');
			setSelectedSupervisor((prev) => prev ? { ...prev, isActive: true } : null);
			fetchSupervisors();
		} catch {
			toast.error('Failed to enable supervisor');
		} finally {
			setActionLoading(null);
		}
	};

	const handleDeleteSupervisor = async () => {
		if (!selectedSupervisor || actionLoading) return;
		setActionLoading('delete');
		try {
			await adminAPI.deleteUser(selectedSupervisor.id);
			toast.success('Supervisor deleted permanently.');
			setShowDeleteConfirm(false);
			setSelectedSupervisor(null);
			fetchSupervisors();
		} catch (err) {
			toast.error(err.response?.data?.message || 'Failed to delete supervisor');
		} finally {
			setActionLoading(null);
		}
	};

	const handleOpenEdit = () => {
		setEditForm({
			firstName: selectedSupervisor.firstName || '',
			lastName: selectedSupervisor.lastName || '',
			email: selectedSupervisor.email || '',
			phone: selectedSupervisor.phone || '',
			ghanaCard: selectedSupervisor.ghanaCard || '',
			address: selectedSupervisor.address || '',
		});
		setShowEditModal(true);
	};

	const handleEditSubmit = async () => {
		if (editLoading) return;
		setEditLoading(true);
		try {
			await adminAPI.updateUser(selectedSupervisor.id, editForm);
			toast.success('Supervisor updated successfully.');
			setShowEditModal(false);
			fetchSupervisors();
			setSelectedSupervisor((prev) => prev ? { ...prev, ...editForm } : null);
		} catch (err) {
			toast.error(err.response?.data?.message || 'Failed to update supervisor');
		} finally {
			setEditLoading(false);
		}
	};

	const handleRemoveAgent = async (agentId) => {
		if (!selectedSupervisor || actionLoading) return;
		setActionLoading(agentId);
		try {
			const remainingIds = assignedAgents.filter((a) => a.id !== agentId).map((a) => a.id);
			await adminAPI.assignAgentsToSupervisor(selectedSupervisor.id, remainingIds);
			toast.success('Agent removed from supervisor');
			fetchAssignedAgents(selectedSupervisor.id);
		} catch (err) {
			toast.error(err.response?.data?.message || 'Failed to remove agent');
		} finally {
			setActionLoading(null);
		}
	};

	const isActive = selectedSupervisor?.isActive !== false;

	if (selectedSupervisor) {
		const agentDisplayName = (a) => {
			const u = a.user || a;
			const n = [u.firstName, u.lastName].filter(Boolean).join(' ').trim();
			return n || u.email || '—';
		};

		return (
			<div className='h-full flex flex-col'>
				<header className='p-6 bg-gradient-to-r from-[#001E3C] to-[#000B18] border-b border-white/10'>
					<div className='flex items-center gap-4 mb-4'>
						<button type='button' onClick={() => setSelectedSupervisor(null)} className='glass-card p-3 rounded-xl'>
							<ChevronLeft className='w-5 h-5 text-white' />
						</button>
						<div className='flex-1'>
							<h2 className='text-xl font-black uppercase text-white'>{getDisplayName(selectedSupervisor)}</h2>
							<p className='text-xs text-white/60'>
								{selectedSupervisor.email}{selectedSupervisor.phone && ` · ${selectedSupervisor.phone}`}
							</p>
							{!isActive && (
								<span className='inline-block mt-1 text-xs font-bold px-2 py-0.5 rounded bg-red-500/30 text-red-300'>
									Disabled
								</span>
							)}
						</div>
					</div>
					<div className='flex gap-2 flex-wrap'>
						{isActive ? (
							<button
								type='button'
								onClick={() => setShowDisableConfirm(true)}
								disabled={!!actionLoading}
								className='flex items-center gap-2 px-4 py-2 rounded-lg bg-red-500/20 text-red-400 border border-red-500/40 text-sm font-bold disabled:opacity-60'
							>
								<UserX className='w-4 h-4' />
								Disable
							</button>
						) : (
							<button
								type='button'
								onClick={handleEnableSupervisor}
								disabled={!!actionLoading}
								className='flex items-center gap-2 px-4 py-2 rounded-lg bg-[#00E676]/20 text-[#00E676] border border-[#00E676]/40 text-sm font-bold disabled:opacity-60'
							>
								<UserCheck className='w-4 h-4' />
								Enable
							</button>
						)}
						<button
							type='button'
							onClick={handleOpenEdit}
							disabled={!!actionLoading}
							className='flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-500/20 text-blue-400 border border-blue-500/40 text-sm font-bold disabled:opacity-60'
						>
							<Edit2 className='w-4 h-4' />
							Edit
						</button>
						<button
							type='button'
							onClick={() => setShowDeleteConfirm(true)}
							disabled={!!actionLoading}
							className='flex items-center gap-2 px-4 py-2 rounded-lg bg-red-900/30 text-red-300 border border-red-700/40 text-sm font-bold disabled:opacity-60'
						>
							<Trash2 className='w-4 h-4' />
							Delete
						</button>
						<button
							type='button'
							onClick={() => navigate('/admin/assign-agents/' + selectedSupervisor.id)}
							className='flex items-center gap-2 px-4 py-2 rounded-lg bg-[#FFC107]/20 text-[#FFC107] border border-[#FFC107]/40 text-sm font-bold'
						>
							<UserPlus className='w-4 h-4' />
							Assign agents
						</button>
					</div>
				</header>

				<main className='flex-1 overflow-y-auto p-6 pb-8 no-scrollbar'>
					<h3 className='text-sm font-bold text-white/80 mb-3 flex items-center gap-2'>
						<Users className='w-4 h-4' />
						Assigned agents ({assignedAgents.length})
					</h3>
					{agentsLoading ? (
						<p className='text-white/50 text-sm'>Loading agents...</p>
					) : assignedAgents.length === 0 ? (
						<p className='text-white/50 text-sm'>No agents assigned. Use "Assign agents" to add agents.</p>
					) : (
						<div className='space-y-3'>
							{assignedAgents.map((a) => (
								<div key={a.id} className='glass-card p-4 border-l-4 border-[#FFC107] flex justify-between items-center gap-3'>
									<div className='min-w-0 flex-1'>
										<p className='font-bold text-white text-sm'>{agentDisplayName(a)}</p>
										<p className='text-xs text-white/60 truncate'>{a.user?.email ?? a.email ?? '—'}</p>
										{a.agentId && <p className='text-xs text-[#FFC107]'>ID: {a.agentId}</p>}
									</div>
									<button
										type='button'
										onClick={() => handleRemoveAgent(a.id)}
										disabled={!!actionLoading}
										className='shrink-0 flex items-center gap-1 px-3 py-2 rounded-lg bg-red-500/20 text-red-400 text-xs font-bold disabled:opacity-60'
									>
										<Trash2 className='w-4 h-4' />
										Remove
									</button>
								</div>
							))}
						</div>
					)}
				</main>

				{/* Edit modal */}
				{showEditModal && (
					<div className='fixed inset-0 z-[2000] flex items-center justify-center p-4 bg-black/70 overflow-y-auto'>
						<div className='glass-card p-6 max-w-sm w-full border border-white/10 my-4'>
							<div className='flex items-center justify-between mb-4'>
								<h3 className='font-black text-white'>Edit Supervisor</h3>
								<button type='button' onClick={() => setShowEditModal(false)} className='text-white/50 hover:text-white text-xl leading-none'>✕</button>
							</div>
							<div className='space-y-3'>
								{[
									{ label: 'First Name', key: 'firstName' },
									{ label: 'Last Name', key: 'lastName' },
									{ label: 'Email', key: 'email', type: 'email' },
									{ label: 'Phone', key: 'phone' },
									{ label: 'Ghana Card', key: 'ghanaCard' },
									{ label: 'Address', key: 'address' },
								].map(({ label, key, type = 'text' }) => (
									<div key={key}>
										<label className='block text-xs text-white/60 mb-1'>{label}</label>
										<input
											type={type}
											value={editForm[key] || ''}
											onChange={(e) => setEditForm((prev) => ({ ...prev, [key]: e.target.value }))}
											className='w-full p-2.5 bg-white/5 border border-white/10 rounded-lg text-white text-sm outline-none'
										/>
									</div>
								))}
							</div>
							<div className='flex gap-3 mt-5'>
								<button type='button' onClick={() => setShowEditModal(false)} className='flex-1 py-2 rounded-lg bg-white/10 text-white text-sm font-bold'>
									Cancel
								</button>
								<button type='button' onClick={handleEditSubmit} disabled={editLoading} className='flex-1 py-2 rounded-lg bg-blue-500 text-white text-sm font-bold disabled:opacity-60'>
									{editLoading ? 'Saving…' : 'Save changes'}
								</button>
							</div>
						</div>
					</div>
				)}

				{/* Delete confirmation modal */}
				{showDeleteConfirm && (
					<div className='fixed inset-0 z-[2000] flex items-center justify-center p-4 bg-black/70'>
						<div className='glass-card p-6 max-w-sm w-full border border-white/10'>
							<div className='flex items-center gap-3 mb-4'>
								<div className='w-12 h-12 rounded-full bg-red-500/20 flex items-center justify-center'>
									<AlertTriangle className='w-6 h-6 text-red-400' />
								</div>
								<div>
									<h3 className='font-black text-white'>Permanently delete supervisor?</h3>
									<p className='text-xs text-white/60'>This cannot be undone.</p>
								</div>
							</div>
							<p className='text-sm text-white/80 mb-4'>
								This will permanently delete <strong className='text-white'>{getDisplayName(selectedSupervisor)}</strong> and all their data.
							</p>
							<div className='flex gap-3'>
								<button type='button' onClick={() => setShowDeleteConfirm(false)} className='flex-1 py-2 rounded-lg bg-white/10 text-white text-sm font-bold'>
									Cancel
								</button>
								<button type='button' onClick={handleDeleteSupervisor} disabled={!!actionLoading} className='flex-1 py-2 rounded-lg bg-red-600 text-white text-sm font-bold disabled:opacity-60'>
									{actionLoading === 'delete' ? 'Deleting…' : 'Delete permanently'}
								</button>
							</div>
						</div>
					</div>
				)}

				{/* Disable confirmation modal */}
				{showDisableConfirm && (
					<div className='fixed inset-0 z-[2000] flex items-center justify-center p-4 bg-black/70'>
						<div className='glass-card p-6 max-w-sm w-full border border-white/10'>
							<div className='flex items-center gap-3 mb-4'>
								<div className='w-12 h-12 rounded-full bg-red-500/20 flex items-center justify-center'>
									<AlertTriangle className='w-6 h-6 text-red-400' />
								</div>
								<div>
									<h3 className='font-black text-white'>Disable this supervisor?</h3>
									<p className='text-xs text-white/60'>They will not be able to log in.</p>
								</div>
							</div>
							<p className='text-sm text-white/80 mb-4'>
								All agents assigned to this supervisor will be unassigned. When they try to log in, they will see: &quot;Your account has been disabled.&quot;
							</p>
							<div className='flex gap-3'>
								<button type='button' onClick={() => setShowDisableConfirm(false)} className='flex-1 py-2 rounded-lg bg-white/10 text-white text-sm font-bold'>
									Cancel
								</button>
								<button type='button' onClick={handleDisableSupervisor} disabled={!!actionLoading} className='flex-1 py-2 rounded-lg bg-red-500 text-white text-sm font-bold disabled:opacity-60'>
									{actionLoading === 'disable' ? 'Disabling…' : 'Disable'}
								</button>
							</div>
						</div>
					</div>
				)}
			</div>
		);
	}

	return (
		<div className='h-full flex flex-col'>
			<header className='p-6 bg-gradient-to-r from-[#001E3C] to-[#000B18] border-b border-white/10'>
				<div className='flex items-center gap-4 mb-4'>
					<button
						type='button'
						onClick={(e) => { e.preventDefault(); navigate('/admin/dashboard'); }}
						className='glass-card p-3 rounded-xl'
					>
						<ChevronLeft className='w-5 h-5 text-white' />
					</button>
					<h2 className='text-xl font-black uppercase text-white'>Supervisor Management</h2>
					<p className='text-xs text-white/60 mt-1'>Click a supervisor to manage agents, disable or enable</p>
				</div>
				<div className='relative'>
					<Search className='absolute left-4 top-3.5 w-5 h-5 text-white/30' />
					<input
						type='text'
						value={searchQuery}
						onChange={(e) => setSearchQuery(e.target.value)}
						className='w-full pl-12 pr-4 py-3 bg-white/5 border border-white/10 rounded-xl outline-none text-white placeholder-white/30'
						placeholder='Search supervisors by name, email, phone...'
					/>
				</div>
			</header>
			<main className='flex-1 overflow-y-auto p-6 pb-8 no-scrollbar'>
				{loading ? (
					<div className='text-center py-12'>
						<p className='text-white/60'>Loading supervisors...</p>
					</div>
				) : (
					<div className='space-y-4'>
						{filteredSupervisors.map((s) => {
							const active = s.isActive !== false;
							return (
								<div
									key={s.id}
									onClick={() => setSelectedSupervisor(s)}
									className={`glass-card p-4 border-l-4 flex items-center gap-4 active:scale-[0.99] transition-transform cursor-pointer ${active ? 'border-[#00E676]' : 'border-white/20'}`}
								>
									<div className='flex-1 min-w-0'>
										<p className='font-bold text-white'>{getDisplayName(s)}</p>
										<p className='text-xs text-white/60'>{s.email}</p>
										{s.phone && <p className='text-xs text-white/50'>{s.phone}</p>}
										<p className='text-xs text-white/50'>{s._count?.supervisedAgents ?? 0} agents</p>
										{!active && <span className='text-xs text-red-400 font-bold'>Disabled</span>}
									</div>
									<ChevronRight className='w-5 h-5 text-white/40 shrink-0' />
								</div>
							);
						})}
						{filteredSupervisors.length === 0 && !loading && (
							<p className='text-center text-white/50 text-sm'>No supervisors found</p>
						)}
					</div>
				)}
			</main>
		</div>
	);
};

export default SupervisorManagement;